import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { SignupRequestSchema, type SignupRequest } from '@seo/types';
import { Button } from '@/components/ui/button';
import { Surface } from '@/components/ui/surface';
import { PasswordField, TextField } from '@/components/ui/form-field';
import { Spinner } from '@/components/spinner';
import { fadeUp, stagger } from '@/components/motion';
import { useSignup } from '@/features/auth/use-signup';
import { AuthLayout } from '@/routes/_auth';
import { summarizeApiError } from '@/lib/api';
import { useStaticTitle } from '@/lib/use-crumbs';

export function SignupRoute(): JSX.Element {
  const navigate = useNavigate();
  const { next } = useSearch({ strict: false }) as { next?: string };
  const signup = useSignup();
  useStaticTitle('Create your workspace');
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignupRequest>({
    resolver: zodResolver(SignupRequestSchema),
    defaultValues: {
      email: '',
      password: '',
      display_name: '',
      tenant_name: '',
    },
  });

  return (
    <AuthLayout>
      <Surface tone="raised" className="w-full">
        <motion.form
          variants={stagger}
          initial="hidden"
          animate="show"
          className="space-y-5 p-7 sm:p-8"
          onSubmit={handleSubmit(async (values) => {
            try {
              await signup.mutateAsync(values);
              await navigate({ to: (next ?? '/') as string });
            } catch (e) {
              const summary = summarizeApiError(e, {
                fallbackTitle: 'Sign up failed',
                fieldLabels: {
                  email: 'Email',
                  password: 'Password',
                  display_name: 'Your name',
                  tenant_name: 'Workspace name',
                },
              });
              toast.error(summary.title, { description: summary.description });
            }
          })}
          noValidate
        >
          <motion.div variants={fadeUp} className="space-y-1.5">
            <h1 className="text-2xl font-semibold tracking-tight">
              Create your workspace
            </h1>
            <p className="text-sm text-muted-foreground">
              Spin up a new tenant in seconds — bring your team in later.
            </p>
          </motion.div>

          <motion.div variants={fadeUp}>
            <TextField
              label="Workspace name"
              autoFocus
              placeholder="Acme Marketing"
              error={errors.tenant_name?.message}
              {...register('tenant_name')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <TextField
              label="Your name"
              autoComplete="name"
              placeholder="Alex Doe"
              error={errors.display_name?.message}
              {...register('display_name')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              error={errors.email?.message}
              {...register('email')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <PasswordField
              label="Password"
              autoComplete="new-password"
              placeholder="At least 10 characters"
              error={errors.password?.message}
              {...register('password')}
            />
          </motion.div>

          <motion.div variants={fadeUp}>
            <Button
              type="submit"
              size="lg"
              className="w-full bg-gradient-primary text-primary-foreground hover:opacity-95"
              disabled={signup.isPending || isSubmitting}
            >
              {signup.isPending ? <Spinner className="mr-2" /> : null}
              Create workspace
              {!signup.isPending ? (
                <ArrowRight className="ml-1 h-4 w-4" />
              ) : null}
            </Button>
          </motion.div>

          <motion.p
            variants={fadeUp}
            className="text-center text-sm text-muted-foreground"
          >
            Have an account?{' '}
            <Link
              to="/login"
              search={next ? { next } : {}}
              className="font-medium text-primary underline-offset-4 hover:underline"
            >
              Sign in
            </Link>
          </motion.p>
        </motion.form>
      </Surface>
    </AuthLayout>
  );
}

